import { Pencil, Plus, X } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useNotify, useRefresh } from "ra-core";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";

const SELECT_CLASS =
  "border-input h-9 rounded-md border bg-transparent px-3 text-sm shadow-xs";

const ID_LABEL: Record<string, string> = {
  nmls: "NMLS",
  dre: "DRE",
  mls: "MLS",
  email: "Email",
  phone: "Phone",
};

const errorCode = (error: unknown) =>
  (error as { body?: { error?: string } }).body?.error ?? "";

/** Primary first, as the API expects the type list. */
const orderedTypes = (types: { type_id: string; is_primary: boolean }[]) =>
  [...types]
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
    .map((t) => t.type_id);

/**
 * Edit a contact's name and types (W10 H2). Types replace the set; the first
 * ticked stays primary.
 */
export function ContactEditForm({
  contact,
}: {
  contact: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    types: { type_id: string; is_primary: boolean }[];
  };
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [isOpen, setIsOpen] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [types, setTypes] = useState<string[]>([]);
  const catalogs = useQuery({
    queryKey: ["catalogs"],
    queryFn: () => dataProvider.getCatalogs(),
    enabled: isOpen,
  });

  const open = () => {
    setFirst(contact.first_name ?? "");
    setLast(contact.last_name ?? "");
    setTypes(orderedTypes(contact.types ?? []));
    setIsOpen(true);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={open}>
        <Pencil className="size-4" />
        Edit contact
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit contact</DialogTitle>
            <DialogDescription>
              Name and types; the first type ticked is the primary one.
            </DialogDescription>
          </DialogHeader>
          <form
            aria-label="Edit contact"
            className="space-y-3"
            onSubmit={async (event) => {
              event.preventDefault();
              try {
                await dataProvider.updateDetails("contacts", contact.id, {
                  first_name: first,
                  last_name: last,
                  types,
                });
                setIsOpen(false);
                refresh();
              } catch (error) {
                notify(
                  errorCode(error) === "name_required"
                    ? "A contact needs a first or last name"
                    : "The contact could not be saved",
                  { type: "error" },
                );
              }
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="edit-first-name">First name</Label>
                <Input
                  id="edit-first-name"
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-last-name">Last name</Label>
                <Input
                  id="edit-last-name"
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                />
              </div>
            </div>
            <fieldset className="space-y-1">
              <legend className="text-sm font-medium">Types</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {(catalogs.data?.contact_types ?? []).map((type) => (
                  <label
                    key={type.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Checkbox
                      checked={types.includes(type.id)}
                      onCheckedChange={(checked) =>
                        setTypes(
                          checked
                            ? [...types, type.id]
                            : types.filter((t) => t !== type.id),
                        )
                      }
                    />
                    {type.label}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="flex gap-2">
              <Button type="submit" size="sm">
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Remove one identifier (W10 H2): it has no history to keep. */
export function RemoveIdentifierButton({
  identifierId,
  label,
}: {
  identifierId: string;
  label: string;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 text-muted-foreground hover:text-foreground"
      aria-label={`Remove ${label}`}
      onClick={async () => {
        try {
          await dataProvider.removeIdentifier(identifierId);
          refresh();
        } catch {
          notify("The identifier could not be removed", { type: "error" });
        }
      }}
    >
      <X className="size-4" />
    </Button>
  );
}

/** Add an email, phone, NMLS, DRE or MLS id to a contact (W10 H2). */
export function AddIdentifierForm({ contactId }: { contactId: string }) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [idType, setIdType] = useState("email");
  const [value, setValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  if (!isOpen) {
    return (
      <Button
        className="mt-3 -ml-2 text-muted-foreground"
        size="sm"
        variant="ghost"
        onClick={() => setIsOpen(true)}
      >
        <Plus className="size-4" />
        Add identifier
      </Button>
    );
  }
  return (
    <form
      aria-label="Add identifier"
      className="mt-3 flex flex-wrap items-end gap-2"
      onSubmit={async (event) => {
        event.preventDefault();
        try {
          await dataProvider.addIdentifier(contactId, {
            id_type: idType,
            value,
          });
          setValue("");
          setIsOpen(false);
          refresh();
        } catch (error) {
          const code = errorCode(error);
          notify(
            code === "identifier_taken"
              ? `That ${ID_LABEL[idType]} already belongs to another contact`
              : code === "invalid_identifier"
                ? `That is not a valid ${ID_LABEL[idType]}`
                : "The identifier could not be added",
            { type: "error" },
          );
        }
      }}
    >
      <select
        aria-label="Identifier type"
        className={SELECT_CLASS}
        value={idType}
        onChange={(e) => setIdType(e.target.value)}
      >
        {Object.entries(ID_LABEL).map(([id, label]) => (
          <option key={id} value={id}>
            {label}
          </option>
        ))}
      </select>
      <Input
        aria-label="Identifier value"
        className="w-56"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Button
        type="submit"
        size="sm"
        variant="outline"
        disabled={!value.trim()}
      >
        Add
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => setIsOpen(false)}
      >
        Cancel
      </Button>
    </form>
  );
}
